import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLFormElement} [form] - The search form element.
 * @property {HTMLElement[]} [searchTabs] - The resource-type filter tab buttons.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * The active resource-type filter tab. One of the predictive-search resource
   * types ('product', 'collection', 'page', 'article') or 'education' — a
   * client-side filtered view of the Education collection.
   * @type {string}
   */
  #activeScope = 'product';

  /**
   * Cached markup for the Education collection (fetched once, filtered locally).
   * @type {string | null}
   */
  #educationMarkup = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      // Close the (non-modal) search when a mega menu opens on hover, so the two
      // overlays don't stack. closeDialog() no-ops when already closed.
      document.addEventListener(ThemeEvents.megaMenuHover, this.#handleMegaMenuHover, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#positionPanel, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    // Reset back to the default Products tab so the next open starts clean.
    if (this.#activeScope !== 'product') {
      this.#activeScope = 'product';
      this.#updateTabState('product');
      this.#updateSearchType('product');
    }
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  /**
   * Closes the search when a mega menu is hovered open in the header.
   */
  #handleMegaMenuHover = () => {
    this.dialog?.closeDialog();
  };

  /**
   * Anchors the (desktop) search panel directly beneath whatever is currently at
   * the top of the viewport. Measured on each open because the header height
   * changes with scroll (the announcement bar scrolls away, leaving a shorter
   * sticky header), so a static `--header-group-height` would leave a gap.
   */
  #positionPanel = () => {
    const header = document.getElementById('header-component');
    const bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    // Set on the <dialog-component> so the panel (child, via inheritance) and
    // its dim overlay (::before on .search-modal) both read it.
    this.dialog?.style.setProperty('--search-panel-top', `${bottom}px`);

    // Cap the scrollable results area to the space left below the sticky search
    // bar (input + tabs), so the last row stays reachable. The flat `65dvh` cap
    // ignores where the panel actually starts, so a tall header / short viewport
    // pushes the bottom of the scroll viewport below the fold. Measured here on
    // open; CSS clamps it against the design cap with min().
    const sticky = this.querySelector('.predictive-search-form__sticky');
    if (sticky) {
      const gap = 16;
      const available = Math.max(0, Math.round(window.innerHeight - sticky.getBoundingClientRect().bottom - gap));
      this.dialog?.style.setProperty('--search-results-max-height', `${available}px`);
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
        // History is now empty, so fall back to the default collection.
        this.resetSearch(false);
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }

    // On the Education tab, clearing the query shows the full course list
    // rather than the default empty state.
    if (this.#activeScope === 'education') {
      this.refs.searchInput.value = '';
      this.#hideResetButton();
      this.#currentIndex = -1;
      this.#showEducationResults('');
      return;
    }

    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (searchTerm.length) {
      this.#showResetButton();
    } else {
      this.#hideResetButton();
    }

    this.#trackQuery(searchTerm);
    this.#runCurrentQuery(searchTerm);
  }, 200);

  /** Last query sent to GA4, so we don't re-fire the same term. */
  #lastTrackedQuery = '';

  /**
   * Report the search term to GA4 (`search`), debounced ~1s beyond the search
   * debounce so we don't log mid-type queries. Minimum length 3, deduped.
   */
  #trackQuery = debounce((searchTerm) => {
    const term = (searchTerm || '').trim();
    if (term.length < 3 || term === this.#lastTrackedQuery) return;
    this.#lastTrackedQuery = term;
    /** @type {any} */ (window).track?.('search', { search_term: term });
  }, 1000);

  /**
   * Switches the active resource-type filter tab and re-runs the current query
   * within the newly selected scope.
   * @param {MouseEvent} event - The click event from a tab button.
   */
  selectTab = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const tab = target.closest('[data-search-scope]');
    if (!(tab instanceof HTMLElement)) return;

    const scope = tab.dataset.searchScope;
    if (!scope || scope === this.#activeScope) return;

    /** @type {any} */ (window).track?.('buff_search_tab_select', {
      tab_title: tab.textContent?.trim(),
      tab_scope: scope,
    });

    // Drop focus from the search input so iOS dismisses the keyboard when a tab
    // is tapped. The input keeps focus otherwise (tabs are <button>s, which iOS
    // doesn't move focus to), so the keyboard would stay up and cover the
    // filtered results the user just switched to.
    this.refs.searchInput.blur();

    this.#activeScope = scope;
    this.#updateTabState(scope);
    this.#updateSearchType(scope);
    this.#currentIndex = -1;
    this.#runCurrentQuery();
  };

  /**
   * Reflects the active scope on the tab buttons.
   * @param {string} scope - The active resource scope.
   */
  #updateTabState(scope) {
    for (const tab of this.refs.searchTabs ?? []) {
      const isActive = tab.dataset.searchScope === scope;
      tab.classList.toggle('predictive-search__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }
  }

  /**
   * Scopes the "view all" form submission to the active resource type. The
   * storefront search page only understands product/article/page; collection
   * and education fall back to an unscoped search.
   * @param {string} scope - The active resource scope.
   */
  #updateSearchType(scope) {
    const { form } = this.refs;
    if (!(form instanceof HTMLFormElement)) return;

    let typeInput = form.querySelector('input[name="type"]');
    const searchableTypes = ['product', 'article', 'page'];

    if (searchableTypes.includes(scope)) {
      if (!(typeInput instanceof HTMLInputElement)) {
        typeInput = document.createElement('input');
        typeInput.type = 'hidden';
        typeInput.name = 'type';
        form.appendChild(typeInput);
      }
      typeInput.value = scope;
    } else if (typeInput) {
      typeInput.remove();
    }
  }

  /**
   * Runs the predictive search for the current term within the active scope.
   * @param {string} [searchTerm] - The term to search for (defaults to input value).
   */
  #runCurrentQuery(searchTerm = this.refs.searchInput.value.trim()) {
    if (this.#activeScope === 'education') {
      this.#showEducationResults(searchTerm);
      return;
    }

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#getSearchResults(searchTerm);
  }

  /**
   * Renders the Education collection, filtered client-side by the search term.
   * The list is fetched once and cached, then matching cards are kept on each
   * keystroke.
   * @param {string} searchTerm - The term to filter course titles by.
   */
  async #showEducationResults(searchTerm) {
    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    if (this.#educationMarkup == null) {
      const markup = await sectionRenderer.getSectionHTML('predictive-search-education', true);
      if (abortController.signal.aborted) return;
      this.#educationMarkup = markup ?? '';
    }

    const parsed = new DOMParser().parseFromString(this.#educationMarkup, 'text/html');
    const dropdown = parsed.querySelector('.predictive-search-dropdown');
    if (!dropdown) return;

    const needle = searchTerm.toLowerCase();
    const cards = dropdown.querySelectorAll('li[data-product-title]');
    let visibleCount = 0;

    for (const card of cards) {
      const title = card instanceof HTMLElement ? card.dataset.productTitle ?? '' : '';
      if (!needle || title.includes(needle)) {
        visibleCount += 1;
      } else {
        card.remove();
      }
    }

    if (visibleCount === 0) {
      const inner = dropdown.querySelector('.predictive-search-results__inner');
      if (inner instanceof HTMLElement) {
        const noResults = parsed.createElement('p');
        noResults.className = 'predictive-search-results__no-results';
        // Mirror the message used by the other tabs (rendered server-side with a
        // `%terms%` placeholder so it can be localised), substituting the current
        // query. textContent keeps the user input safe from injection.
        const template = inner.dataset.noResultsText || 'No results found.';
        noResults.textContent = template.replace('%terms%', searchTerm);
        inner.replaceChildren(noResults);
      }
    }

    if (abortController.signal.aborted) return;

    // Morph the section wrapper (the dropdown's parent), not the dropdown itself:
    // morph runs children-only, so passing the dropdown would strip the
    // `.predictive-search-dropdown` wrapper that carries the page-gutter inline
    // padding. The products path morphs the full section markup (whose first
    // child is the wrapper), so preserving the dropdown here keeps the Education
    // results aligned with the Products results.
    morph(predictiveSearchResults, dropdown.parentElement ?? dropdown);
    this.#resetScrollPositions();
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[limit]', '10');
    // Scope the predictive results to the active tab's resource type. The
    // suggested-search pills (`query` resource) are only wanted on the Products
    // tab, so request them alongside products there and restrict every other tab
    // to its own resource type.
    if (this.#activeScope === 'product') {
      url.searchParams.set('resources[type]', 'product,query');
    } else if (this.#activeScope && this.#activeScope !== 'education') {
      url.searchParams.set('resources[type]', this.#activeScope);
    }

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      // When the shopper has browsing history, show *only* Recently Viewed.
      // The fallback collection is kept solely for first-time visitors (the
      // `viewedProducts.length > 0` guard above), so replace rather than prepend.
      collectionElement.replaceChildren(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
